<section class="page">
  <div class="page-header">
    <div>
      <h3 class="title">Lobby Themes</h3>
      <p class="subtitle">Manage the themes used across lobbies.</p>
    </div>

  </div>

  <div class="toolbar-container">
    <div class="left-controls d-flex align-items-center gap-3">
      <app-search-box tooltipText="Search By name and description" (search)="onSearch($event)"></app-search-box>

      <p-select
        [options]="themeStatuses"
        [(ngModel)]="selectedThemeStatus"
        optionLabel="label"
        optionValue="value"
        [showClear]="true"
        class="theme-status-dropdown"
        placeholder="Select status"
        (onChange)="filterChanged()">
      </p-select>

      @if (hasSelection()) {
        <button class="btn btn-icon btn-delete-bulk" (click)="deleteSelectedThemes()" title="Delete Selected">
          <i class="pi pi-trash" style="font-size: 1.25rem;"></i>
        </button>
      }
    </div>

    <div class="d-flex align-items-center gap-3 ms-auto">
      <button class="btn btn-primary" (click)="openCreateModal()">
        <i class="fa-solid fa-plus me-2"></i>Create New Theme
      </button>
      <div class="d-flex align-items-center gap-2 border rounded px-1 header-icon-actions-wrapper">
        <button type="button" class="btn btn-icon p-1 header-icon-action-btn" (click)="fetchThemes()" title="Refresh">
          <i class="pi pi-refresh" style="font-size: 1.25rem; color: #6b7280;"></i>
        </button>
        <button type="button" class="btn btn-icon p-1 tooltip-wrap header-icon-action-btn" (click)="toggleIncludeDeleted()">
          <i class="pi pi-asterisk" style="font-size: 1.25rem;" [style.color]="includeDeleted() ? '#ec4899' : '#6b7280'"></i>
          <span class="tooltip-box">Include Mark Deleted</span>
        </button>
      </div>
    </div>
  </div>
  <div class="table-container shadow">
    <table class="themes-table">
      <thead>
        <tr>
          <th class="checkbox-col" width="32">
            <input type="checkbox" [checked]="allSelected()" (change)="onToggleSelectAll()" />
          </th>
          <th class="sortable" (click)="onSort('name')">
            Theme Name <span class="sort-indicator" [class.active]="currentSortField() === 'name'">{{ getSortIcon('name') }}</span>
          </th>
          <th >
            Description
          </th>
          <th class="sortable" (click)="onSort('participantsCount')">
            Participants <span class="sort-indicator" [class.active]="currentSortField() === 'participantsCount'">{{ getSortIcon('participantsCount') }}</span>
          </th>
          <th class="sortable" (click)="onSort('interestsCount')">
            Interested <span class="sort-indicator" [class.active]="currentSortField() === 'interestsCount'">{{ getSortIcon('interestsCount') }}</span>
          </th>
          <th class="sortable" (click)="onSort('entryFee')">
            Entry Fee <span class="sort-indicator" [class.active]="currentSortField() === 'entryFee'">{{ getSortIcon('entryFee') }}</span>
          </th>
          <th class="sortable" (click)="onSort('status')">
            Status <span class="sort-indicator" [class.active]="currentSortField() === 'status'">{{ getSortIcon('status') }}</span>
          </th>
          <th class="text-center">Action</th>
        </tr>
      </thead>
      <tbody>
        @if (this.loading()) {
          <tr>
            <td colspan="100%">
              <app-spinner-loader [width]="35"></app-spinner-loader>
            </td>
          </tr>
        } @else if (this.themes().length === 0) {
          <tr>
            <td colspan="100%" class="no-data-cell">No theme found</td>
          </tr>
        } @else {
          @for (t of themes(); track trackById($index, t)) {
            <tr [ngClass]="{'deleted-row': !!t.deletedAt}" [class.selected]="t.selected">
              <td class="checkbox-col">
                <input type="checkbox" [(ngModel)]="t.selected" (change)="updateSelectAllState()"  />
              </td>
              <td>
                <div class="d-flex align-items-center gap-2">
                  <img class="avatar" style="cursor: pointer;" [src]="t.image?.signedUrl || '/images/user.png'" alt="icon" (click)="openImagePreview(t.image?.signedUrl)" />
                  <span class="fw-semibold">{{ t.name }}</span>
                </div>
              </td>
              <td>
                <p class="notif-msg" [pTooltip]="t.description || 'N/A'" tooltipPosition="top">
                  {{ t.description ? ((t.description | slice:0:50) + (t.description.length > 50 ? '...' : '')) : 'N/A' }}
                </p>
              </td>
              <td class="muted">{{ t.defaults?.participantsCount ?? "N/A" }}</td>
              <td class="muted">{{ t.defaults?.interestsCount ?? "N/A" }}</td>
              <td class="muted">{{ t.defaults?.entryFee ?? "N/A" }}</td>
              <td>
                <div class="toggle-container">
                  <label class="switch">
                    <input type="checkbox" [checked]="t.isActive" disabled="true" />
                    <span class="slider"></span>
                  </label>
                </div>
              </td>
              <td class="center action-buttons">
                <button class="btn btn-edit" (click)="openEditModal(t)" [disabled]="!!t.deletedAt">
                  Edit
                </button>
              </td>
            </tr>
          }
        }
      </tbody>
    </table>

    <!-- Pagination Section -->
    <div class="pagination-section">
      <div class="pagination-info">
        <span>
          Showing {{ startIndex() }} to {{ endIndex() }} of
          {{ totalCount() }} Entries
        </span>
      </div>
      <div class="d-flex gap-2 align-items-center">
        @if (themes().length !== 0) {
          <label class="page-size-label" for="pageSizeSelect">Page Size:</label>
          <select id="pageSizeSelect" [(ngModel)]="pageSize" (ngModelChange)="onPageSizeChange($event)"
            class="form-select" style="width: auto;">
            @for (size of pageSizes; track size) {
              <option [value]="size">{{ size }}</option>
            }
          </select>
        }
        <button class="btn btn-outline pagination-btn"
          [disabled]="currentPage() === 1 || loading() || themes().length === 0" (click)="previousPage()">
          &lt; Prev
        </button>
        <button class="btn btn-outline pagination-btn" [disabled]="currentPage === totalPages || loading()"
          (click)="nextPage()">
          Next &gt;
        </button>
      </div>
    </div>

  </div>

</section>

@if (showThemeForm()) {
  <app-theme-form [isVisible]="showThemeForm()" [isEditMode]="isEditMode()" [theme]="editingTheme()"
  [isLoading]="isSubmitting()" (close)="closeThemeForm()" (formSubmit)="onThemeFormSubmit($event)"></app-theme-form>
}

@if (previewImageUrl()) {
  <div class="img-preview-backdrop" (click)="closePreview()">
    <div class="img-preview-box" (click)="$event.stopPropagation()">
      <button class="img-preview-close" (click)="closePreview()">
        <i class="fa-solid fa-xmark"></i>
      </button>
      <img [src]="previewImageUrl()!" alt="Preview" class="img-preview-full"
        (click)="openImageInNewTab(previewImageUrl()!)" title="Click to view in new tab" />
      </div>
    </div>
  }